import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, set } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

export default function DreamFeaturePage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [profileSummary, setProfileSummary] = useState('');
  const [profileAnalysis, setProfileAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Simulation State
  const [activeScenario, setActiveScenario] = useState(null);
  const [turn, setTurn] = useState(0); // 0: Select, 1: Turn 1, 2: Turn 2, 3: Completed
  const [history, setHistory] = useState([]);
  const [narrative, setNarrative] = useState('');
  const [question, setQuestion] = useState('');
  const [userResponse, setUserResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Real-time Scores
  const [technicalScore, setTechnicalScore] = useState(50);
  const [leadershipScore, setLeadershipScore] = useState(50);
  const [strategyScore, setStrategyScore] = useState(50);
  const [evaluationFeedback, setEvaluationFeedback] = useState('');
  const [finalGrade, setFinalGrade] = useState('');
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [savedSimulations, setSavedSimulations] = useState([]);

  const chatEndRef = useRef(null);

  const scenarios = [
    {
      id: 'black-friday-outage',
      title: '🚨 Black Friday System Outage',
      role: 'Software Engineer',
      description: 'The payment processing system goes down at midnight during peak checkout volume. You have minutes to isolate the bug and manage stakeholders.'
    },
    {
      id: 'yc-pitch-crisis',
      title: '💼 The YC Demo Day Pivot',
      role: 'Startup Founder / CEO',
      description: 'A lead partner challenges your primary market size 10 minutes before you step on stage. Pivot your pitch or defend your vision.'
    },
    {
      id: 'product-pivot-dispute',
      title: '🎨 The Design vs Engineering Split',
      role: 'Product / Project Lead',
      description: 'Engineers claim a critical UI feature is impossible in the sprint timeline; designers claim it is a dealbreaker. Resolve the conflict.'
    }
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Fetch profile
        const db = getDatabase();
        const refAnalysis = ref(db, `resumeAnalysis/${user.uid}`);
        try {
          const snap = await get(refAnalysis);
          if (snap.exists()) {
            const data = snap.val();
            setProfileSummary(data.summary || '');
            setProfileAnalysis(data.analysis || null);
            if (data.dreamSimulations) {
              setSavedSimulations(data.dreamSimulations);
            }
          }
        } catch (err) {
          console.error("Error loading user profile:", err);
        }

        // Fetch pro status
        const refProfile = ref(db, `users/${user.uid}`);
        try {
          const snap = await get(refProfile);
          if (snap.exists() && snap.val().proExpiryDate) {
            const expiry = new Date(snap.val().proExpiryDate);
            if (expiry > new Date()) {
              setIsPro(true);
            }
          }
        } catch (err) {
          console.error("Error checking pro:", err);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history, narrative]);

  const handleStartScenario = async (scenario) => {
    setActiveScenario(scenario);
    setTurn(1);
    setHistory([]);
    setTechnicalScore(50);
    setLeadershipScore(50);
    setStrategyScore(50);
    setEvaluationFeedback('Initializing simulation scenario...');
    setSubmitting(true);

    try {
      const response = await fetch('https://coursehub-282e6.uc.r.appspot.com/api/simulate-career', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenarioId: scenario.title,
          turn: 1,
          history: [],
          profileSummary: profileSummary || 'A professional looking to grow their career.',
          analysis: profileAnalysis || { technical_skills: 50, leadership_people_mgmt: 50, product_project_mgmt: 50 }
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setNarrative(result.narrative);
        setQuestion(result.question);
        setHistory([{ role: 'model', parts: [{ text: result.narrative + '\n\n' + result.question }] }]);
      } else {
        alert(result.message || 'Failed to start scenario.');
        setTurn(0);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to connect to simulator backend.');
      setTurn(0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextTurn = async () => {
    if (!userResponse.trim()) return;
    setSubmitting(true);
    const newHistory = [...history, { role: 'user', parts: [{ text: userResponse }] }];
    setHistory(newHistory);
    const currentResponse = userResponse;
    setUserResponse('');

    const nextTurn = turn + 1;

    try {
      const response = await fetch('https://coursehub-282e6.uc.r.appspot.com/api/simulate-career', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenarioId: activeScenario.title,
          turn: turn,
          history: newHistory,
          profileSummary: profileSummary,
          analysis: profileAnalysis,
          userResponse: currentResponse
        })
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setNarrative(result.narrative);
        setQuestion(result.question);
        
        // Update Scores
        if (result.evaluation) {
          const evalData = result.evaluation;
          setTechnicalScore(prev => Math.min(100, Math.max(0, prev + (evalData.technicalDelta || 0))));
          setLeadershipScore(prev => Math.min(100, Math.max(0, prev + (evalData.leadershipDelta || 0))));
          setStrategyScore(prev => Math.min(100, Math.max(0, prev + (evalData.strategyDelta || 0))));
          setEvaluationFeedback(evalData.feedback || '');

          if (nextTurn === 3) {
            setFinalGrade(evalData.finalGrade || 'B');
            setRecommendedCourses(evalData.recommendedCourses || []);
          }
        }

        setHistory(prev => [...prev, { role: 'model', parts: [{ text: result.narrative + '\n\n' + result.question }] }]);
        setTurn(nextTurn);

        // If completed, save simulator results to DB
        if (nextTurn === 3) {
          const db = getDatabase();
          const refSims = ref(db, `resumeAnalysis/${currentUser.uid}/dreamSimulations`);
          const newSimRecord = {
            scenario: activeScenario.title,
            grade: result.evaluation?.finalGrade || 'B',
            date: new Date().toLocaleDateString(),
            tech: Math.min(100, technicalScore + (result.evaluation?.technicalDelta || 0)),
            leadership: Math.min(100, leadershipScore + (result.evaluation?.leadershipDelta || 0)),
            strategy: Math.min(100, strategyScore + (result.evaluation?.strategyDelta || 0))
          };
          const updated = [...savedSimulations, newSimRecord];
          setSavedSimulations(updated);
          await set(refSims, updated);
        }
      } else {
        alert(result.message || 'Error executing next turn.');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to connect to simulator backend.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#673ab7' }}>Booting AI Simulator...</p>
      </div>
    );
  }

  if (!profileAnalysis) {
    return (
      <div style={{ padding: '60px 40px', maxWidth: '600px', margin: '80px auto', textAlign: 'center', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <span style={{ fontSize: '48px' }}>🔒</span>
        <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', marginTop: '20px' }}>Simulator Locked</h2>
        <p style={{ fontSize: '14.5px', color: '#64748b', lineHeight: '1.6', margin: '15px 0 25px 0' }}>
          Please upload your resume or import your LinkedIn profile first so our simulator can customize high-stakes scenarios for your actual job role.
        </p>
        <button
          onClick={() => navigate('/mentorai')}
          style={{
            padding: '12px 24px',
            background: '#673ab7',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '30px 40px', fontFamily: 'system-ui, sans-serif' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <button 
            onClick={() => navigate('/mentorai')}
            style={{ background: 'none', border: 'none', color: '#673ab7', fontWeight: '700', cursor: 'pointer', fontSize: '14px', padding: 0, display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            ← Return to Dashboard
          </button>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: '5px 0 0 0' }}>
            ⚡ GuideAI Interactive Career Simulator
          </h1>
        </div>
        {isPro ? (
          <span style={{ background: '#dcfce7', color: '#166534', fontWeight: '700', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', border: '1px solid #bbf7d0' }}>
            👑 PRO MEMBER
          </span>
        ) : (
          <button
            onClick={() => navigate('/upgrade')}
            style={{ background: 'linear-gradient(135deg, #ffc107, #ff9800)', border: 'none', color: '#ffffff', fontWeight: '800', padding: '8px 16px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', boxShadow: '0 4px 10px rgba(255,152,0,0.3)' }}
          >
            🌟 Upgrade to PRO
          </button>
        )}
      </div>

      {/* Main Grid */}
      {turn === 0 ? (
        // Scenario Selection Screen
        <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '30px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#475569', marginBottom: '20px' }}>Select Simulation Scenario:</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {scenarios.map((s) => (
                <div 
                  key={s.id}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    padding: '25px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>{s.title}</h3>
                    <span style={{ fontSize: '11px', fontWeight: '700', background: '#f3e8ff', color: '#673ab7', padding: '3px 8px', borderRadius: '10px' }}>
                      Target: {s.role}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b', lineHeight: '1.5' }}>{s.description}</p>
                  <button
                    onClick={() => handleStartScenario(s)}
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: '8px',
                      padding: '10px 20px',
                      background: '#673ab7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '13px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 10px rgba(103, 58, 183, 0.2)'
                    }}
                  >
                    Boot Simulation →
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar: Past Simulation Results */}
          <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '25px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>🏆 Past Results</h3>
            {savedSimulations && savedSimulations.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {savedSimulations.map((sim, idx) => (
                  <div key={idx} style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '15px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13.5px', fontWeight: '700', color: '#1e293b' }}>{sim.scenario.substring(0, 24)}...</span>
                      <span style={{ fontSize: '15px', fontWeight: '800', color: '#673ab7' }}>Grade: {sim.grade}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Completed on {sim.date}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b', textAlign: 'center', padding: '20px 0' }}>No simulations completed yet.</p>
            )}
          </div>
        </div>
      ) : (
        // Active Scenario Simulation Interface
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr', gap: '30px' }}>
          
          {/* Main Simulator Console */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{
              background: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: '16px',
              height: '520px',
              padding: '25px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
            }}>
              {/* Terminal Screen Output */}
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px', paddingRight: '5px', marginBottom: '15px' }}>
                {history.map((msg, idx) => (
                  <div 
                    key={idx} 
                    style={{
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      background: msg.role === 'user' ? '#334155' : 'transparent',
                      color: msg.role === 'user' ? '#ffffff' : '#f8fafc',
                      borderRadius: '12px',
                      padding: msg.role === 'user' ? '12px 16px' : '0',
                      fontSize: '14.5px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-line',
                      fontFamily: msg.role === 'user' ? 'system-ui, sans-serif' : 'Courier, monospace'
                    }}
                  >
                    {msg.role === 'model' && (
                      <span style={{ color: '#a78bfa', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                        [SYSTEM COACH]
                      </span>
                    )}
                    {msg.parts[0].text}
                  </div>
                ))}
                {submitting && (
                  <p style={{ color: '#a78bfa', fontFamily: 'Courier, monospace', margin: 0 }}>
                    &gt; Processing decisions...
                  </p>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Action input panel */}
              {turn < 3 ? (
                <div style={{ display: 'flex', gap: '10px', borderTop: '1px solid #1e293b', paddingTop: '15px' }}>
                  <input
                    type="text"
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleNextTurn()}
                    disabled={submitting}
                    placeholder="Type your response action here..."
                    style={{
                      flex: 1,
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      color: '#ffffff',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleNextTurn}
                    disabled={submitting || !userResponse.trim()}
                    style={{
                      backgroundColor: '#673ab7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0 24px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Execute Action
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '15px', borderTop: '1px solid #1e293b', paddingTop: '15px' }}>
                  <button
                    onClick={() => setTurn(0)}
                    style={{
                      flex: 1,
                      backgroundColor: '#334155',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Close Simulation
                  </button>
                  <button
                    onClick={() => handleStartScenario(activeScenario)}
                    style={{
                      flex: 1,
                      backgroundColor: '#673ab7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Replay Scenario
                  </button>
                </div>
              )}
            </div>

            {/* Recommended Goal Courses (Visible on Complete) */}
            {turn === 3 && recommendedCourses.length > 0 && (
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '25px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '800', color: '#673ab7' }}>
                  🎓 Suggested Skill Bridging Courses
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  {recommendedCourses.map((c, idx) => (
                    <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '15px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '700', color: '#1e293b' }}>{c.title}</h4>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#673ab7' }}>{c.provider} • {c.difficulty}</span>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>{c.description}</p>
                      <a 
                        href={`https://www.google.com/search?q=${encodeURIComponent(c.title + ' ' + c.provider)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ alignSelf: 'flex-start', fontSize: '12px', fontWeight: '700', color: '#673ab7', textDecoration: 'none', marginTop: '4px' }}
                      >
                        Explore ↗
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Simulation Sidebar: Real-time Evaluation Reports & Scores */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Real-time Dashboard Scores */}
            <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '25px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', fontWeight: '800', color: '#1e293b' }}>📊 Simulation Scoreboard</h3>
              
              {/* Score Bars */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>
                    <span>Technical Execution</span>
                    <span>{technicalScore}/100</span>
                  </div>
                  <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${technicalScore}%`, background: 'linear-gradient(90deg, #3b82f6, #06b6d4)', borderRadius: '4px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>
                    <span>Leadership & Soft Skills</span>
                    <span>{leadershipScore}/100</span>
                  </div>
                  <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${leadershipScore}%`, background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '4px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>
                    <span>Strategic Vision</span>
                    <span>{strategyScore}/100</span>
                  </div>
                  <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${strategyScore}%`, background: 'linear-gradient(90deg, #8b5cf6, #d946ef)', borderRadius: '4px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Coaching Feedback */}
            <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '25px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', flex: 1 }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', fontWeight: '800', color: '#1e293b' }}>🎙️ Coach Feedback</h3>
              <p style={{ margin: 0, fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                {evaluationFeedback || 'Your moves will be analyzed here in real-time.'}
              </p>

              {turn === 3 && (
                <div style={{ marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#64748b' }}>FINAL SIMULATION GRADE</div>
                  <div style={{ fontSize: '48px', fontWeight: '900', color: '#673ab7' }}>{finalGrade}</div>
                  
                  {isPro ? (
                    <div style={{ border: '2px dashed #bbf7d0', borderRadius: '12px', padding: '15px', background: '#f0fdf4', textAlign: 'center' }}>
                      <span style={{ fontSize: '24px' }}>🏆</span>
                      <h4 style={{ margin: '5px 0 0 0', fontSize: '13px', fontWeight: '700', color: '#166534' }}>Verification Certificate Issued</h4>
                      <p style={{ margin: 0, fontSize: '11px', color: '#14532d' }}>This scenario completion badge has been verified in your dashboard.</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/upgrade')}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #673ab7, #9c27b0)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontWeight: '700',
                        fontSize: '13px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(156,39,176,0.3)'
                      }}
                    >
                      🔒 Lock In Verified Certificate (Requires Pro)
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
